import React, { useState, useRef, useEffect } from 'react'
import { drawCurvedLine } from '../../utils/drawLine'
import { motion } from 'framer-motion'
import './styles.css'
import { CloseButton } from 'react-bootstrap'

interface Pair {
  left: any
  right: any
}

interface MatchingPairGameProps {
  pairs: Pair[]
  handleNextExample: () => void
}

const MatchingPairGame2: React.FC<MatchingPairGameProps> = ({ pairs, handleNextExample }) => {
  console.log(pairs, 'additionalPair')

  const [selectedLeft, setSelectedLeft] = useState<string | null>(null)
  const [selectedRight, setSelectedRight] = useState<string | null>(null)
  const [matchedPairs, setMatchedPairs] = useState<Pair[]>([])
  const [attempts, setAttempts] = useState<number>(0)
  const [gameOver, setGameOver] = useState(false)
  const [randomizedLeft, setRandomizedLeft] = useState<string[]>([])
  const [randomizedRight, setRandomizedRight] = useState<string[]>([])
  const [showSuccessModal, setShowSuccessModal] = useState(false) // State for modal
  const [progress, setProgress] = useState(0) // Track the progress of the countdown

  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        handleNextExample() // Transition to the next example
      }, 5000)

      const progressInterval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress < 100) return prevProgress + 2
          return prevProgress
        })
      }, 100)

      return () => {
        clearTimeout(timer)
        clearInterval(progressInterval)
      }
    }
  }, [showSuccessModal])

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const shuffleArray = (array: any[]): any[] => {
    const arr = [...array]
    const randomValues = new Uint32Array(arr.length)
    crypto.getRandomValues(randomValues)

    for (let i = arr.length - 1; i > 0; i--) {
      const j = randomValues[i] % (i + 1)
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }

    return arr
  }

  // Randomize pairs for left and right columns
  useEffect(() => {
    const shuffledPairs = shuffleArray([...pairs])
    setRandomizedLeft(shuffledPairs.map((pair) => pair.left))
    setRandomizedRight(shuffledPairs.map((pair) => pair.right))
  }, [pairs])

  const handleLeftSelect = (left: string) => {
    setSelectedLeft(left)
  }

  const handleRightSelect = (right: string) => {
    setSelectedRight(right)
  }

  const checkMatch = () => {
    if (selectedLeft && selectedRight) {
      const match = pairs.find((pair) => pair.left === selectedLeft && pair.right === selectedRight)

      setAttempts((prev) => prev + 1)

      if (match) {
        setMatchedPairs((prev) => [...prev, match])
        if (matchedPairs.length + 1 === pairs.length) {
          setGameOver(true)
          setShowSuccessModal(true) // Show success modal when game is over
        }
      }

      setSelectedLeft(null)
      setSelectedRight(null)
    }
  }

  useEffect(() => {
    checkMatch()
  }, [selectedLeft, selectedRight])

  useEffect(() => {
    const leftItems = pairs.map((pair) => pair.left)
    const rightItems = pairs.map((pair) => pair.right)

    setRandomizedLeft(shuffleArray(leftItems))
    setRandomizedRight(shuffleArray(rightItems))
  }, [pairs])

  const drawLines = () => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = container.offsetWidth
    canvas.height = container.offsetHeight

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    matchedPairs.forEach(({ left, right }) => {
      const leftEl = document.getElementById(left)
      const rightEl = document.getElementById(right)

      if (leftEl && rightEl) {
        const leftRect = leftEl.getBoundingClientRect()
        const rightRect = rightEl.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()

        const startX = leftRect.right - containerRect.left
        const startY = leftRect.top + leftRect.height / 2 - containerRect.top
        const endX = rightRect.left - containerRect.left
        const endY = rightRect.top + rightRect.height / 2 - containerRect.top

        const color = gameOver
          ? matchedPairs.some((pair) => pair.left === left && pair.right === right)
            ? '#28a745' // green
            : '#dc3545' // red
          : '#007bff' // blue

        drawCurvedLine(startX, startY, endX, endY, color, ctx)
      }
    })
  }

  const handleModalClose = () => {
    setShowSuccessModal(false)
    setProgress(0)
  }

  useEffect(() => {
    drawLines()
  }, [matchedPairs, gameOver])

  return (
    <div className='py-5 position-relative' ref={containerRef}>
      <canvas
        ref={canvasRef}
        className='position-absolute w-100 h-100'
        style={{
          pointerEvents: 'none',
          zIndex: 1 // Ensure the lines are above the cards
        }}
      />
      <div className='row'>
        {/* Left Column: Cards */}
        <div className='col-5' style={{ paddingRight: '20px', position: 'relative' }}>
          {' '}
          {/* Added relative position */}
          <div className='d-flex flex-column gap-10'>
            {randomizedLeft.map((left) => (
              <motion.div
                key={left}
                id={left}
                className={`cursor-pointer text-center border transition-all duration-300 ${
                  selectedLeft === left ? 'bg-primary text-white transform scale-105' : 'bg-light'
                } ${
                  matchedPairs.some((p) => p.left === left) ? 'bg-success text-white' : ''
                } hover:shadow-lg hover:bg-info`}
                onClick={() => handleLeftSelect(left)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{ position: 'relative', zIndex: 2 }} // Ensures the cards are above the line
              >
                <div
                  className='d-flex justify-content-center align-items-center'
                  style={{ height: '80px' }}
                >
                  <p className='m-0' style={{ fontSize: '1.5rem' }}>
                    {left}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Center Divider */}
        <div className='col-2 d-flex justify-content-center align-items-center'>
          <div className='border-start border-3' style={{ height: '100%' }}></div>
        </div>

        {/* Right Column: Large Image Cards */}
        <div className='col-5' style={{ paddingLeft: '20px', position: 'relative' }}>
          {' '}
          {/* Added relative position */}
          <div className='d-flex flex-column gap-4'>
            {randomizedRight.map((right) => (
              <motion.div
                key={right}
                id={right}
                className={`p-4 rounded-lg shadow-lg cursor-pointer text-center border transition-all duration-300 ${
                  selectedRight === right ? 'bg-primary text-white transform scale-105' : 'bg-light'
                } ${
                  matchedPairs.some((p) => p.right === right) ? 'bg-success text-white' : ''
                } hover:shadow-lg hover:bg-info`}
                onClick={() => handleRightSelect(right)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{ position: 'relative', zIndex: 2 }} // Ensures the cards are above the line
              >
                <img
                  src={right}
                  alt='pair'
                  className='img-fluid rounded-lg shadow-sm'
                  style={{ maxHeight: '250px', width: 'auto' }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className='party-animation'>
          <div className='party-popup'>
            <div className='text-end close-button-container mb-2'>
              <CloseButton onClick={handleModalClose} />
            </div>
            <h2>सही उत्तर! आपने सही विकल्प चुना है।</h2>
            <div className='party-cracker'>🎉</div>
            <button onClick={handleNextExample} className='btn btn-success mt-4'>
              धन्यवाद
            </button>
            <div className='progress-bar success'>
              <div className='progress-bar-inner' style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MatchingPairGame2
